/**
 * Pure Gmail conversation turn handler (V1.5).
 * AshleyInterfaceProvider supplies I/O; this module owns dialogue state.
 */

import {
  formatAmbiguousSenderClarification,
  formatAllUnreadSenders,
  formatEmailBrowseOffer,
  formatEmailBrowseReprompt,
  formatEmailDetailSummary,
  formatEmailNotFoundOnPage,
  formatEmailSenderPage,
  formatFullEmailSpeech,
  formatSingleEmailRichSummary,
  getEmailPage,
  hasEmailNextPage,
  EMAIL_PAGE_SIZE,
  EMAIL_WHAT_NEXT_AFTER,
} from "../../connectedServices/email/formatEmailVoiceSummary";
import {
  createGmailConversationState,
  getActiveBrowsePage,
  returnToInboxBrowse,
  selectEmailInState,
  setAmbiguousSenderState,
} from "../../connectedServices/email/gmailConversationState";
import { resolveEmailBrowseChoice } from "./ashleyEmailBrowsePhrases";
import {
  resolveAmbiguousSenderChoice,
  resolveEmailSelection,
} from "./resolveEmailSelection";
import {
  isAshleyAffirmationPhrase,
  isAshleyDeclinePhrase,
} from "./ashleyFollowUpPhrases";
import {
  CONNECTED_SERVICES_UNAVAILABLE_SPEECH,
  isConnectedServicesUiEnabled,
} from "../../connectedServices/connectedServicesGate";

/**
 * @typedef {object} GmailTurnResult
 * @property {import("../../connectedServices/email/gmailConversationState").GmailConversationState|null} state
 * @property {string} speech
 * @property {"none"|"close"|"open_inbox"|"open_message"|"fetch_detail"} action
 * @property {string|null} [messageId]
 */

function browseSpeech(state, pageOverride) {
  const page =
    pageOverride ||
    getEmailPage(state.messages, state.offset, state.pageSize || EMAIL_PAGE_SIZE);
  const clause = formatEmailSenderPage(page, {
    isFirstPage: state.offset === 0,
    totalUnread: state.messages.length,
    offset: state.offset,
  });
  return `${clause}\n\n${formatEmailBrowseOffer()}`;
}

/**
 * Build Level-3 speech after a message is selected (snippet fallback until body loads).
 * @param {object} message
 * @param {string|null} bodyText
 */
export function buildRichSummarySpeech(message, bodyText) {
  return formatSingleEmailRichSummary(message, bodyText || undefined);
}

/**
 * @param {string} transcript
 * @param {import("../../connectedServices/email/gmailConversationState").GmailConversationState} state
 * @param {{ bodyText?: string|null, bodyReady?: boolean }} [extras]
 * @returns {GmailTurnResult}
 */
export function handleGmailBrowseTurn(transcript, state, extras = {}) {
  if (!isConnectedServicesUiEnabled()) {
    return {
      state: state || null,
      speech: CONNECTED_SERVICES_UNAVAILABLE_SPEECH,
      action: "none",
    };
  }

  const current =
    state?.browsingMode === "review_all"
      ? createGmailConversationState({ ...state, browsingMode: "review_all" })
      : createGmailConversationState(state || {});

  // --- Clarify duplicate sender ---
  if (current.browsingMode === "clarify_sender" && current.pendingAmbiguous) {
    const choice = resolveAmbiguousSenderChoice(transcript);
    const matches = current.pendingAmbiguous.matches || [];
    if (!choice || matches.length === 0) {
      return {
        state: current,
        speech: formatAmbiguousSenderClarification(
          current.pendingAmbiguous.senderKey,
          matches.length || 2,
        ),
        action: "none",
      };
    }
    const picked = choice === "newest" ? matches[0] : matches[matches.length - 1];
    const page = getActiveBrowsePage(current);
    const ordinal = page.findIndex((m) => m === picked || m?.id === picked?.id) + 1;
    const nextState = selectEmailInState(
      current,
      picked,
      ordinal > 0 ? ordinal : 1,
    );
    return {
      state: nextState,
      speech: buildRichSummarySpeech(picked, null),
      action: picked?.id ? "fetch_detail" : "none",
      messageId: picked?.id || null,
    };
  }

  // --- Single-email mode ---
  if (current.browsingMode === "single_email" && current.selectedEmail) {
    const selection = resolveEmailSelection(transcript, getActiveBrowsePage(current));
    const normalized = String(transcript || "")
      .toLowerCase()
      .replace(/['']/g, "")
      .replace(/\s+/g, " ")
      .trim();

    if (selection?.kind === "go_back") {
      const restored = returnToInboxBrowse(current);
      return {
        state: restored,
        speech: browseSpeech(restored),
        action: "none",
      };
    }

    // Opening the message in Gmail takes priority over yes/full-read.
    if (/\bopen it\b/.test(normalized) || normalized === "open it") {
      return {
        state: current,
        speech: "Opening that email.",
        action: "open_message",
        messageId: current.selectedMessageId,
      };
    }

    if (current.awaitingFullRead) {
      const wantsFullRead =
        isAshleyAffirmationPhrase(transcript) ||
        /^(read it|read the full email|yes read it|read)$/i.test(normalized);

      if (wantsFullRead) {
        const body =
          extras.bodyText != null ? extras.bodyText : current.bodyText;
        if (body == null && current.selectedMessageId && !extras.bodyReady) {
          return {
            state: current,
            speech: "",
            action: "fetch_detail",
            messageId: current.selectedMessageId,
            pendingFullRead: true,
          };
        }
        const nextState = createGmailConversationState({
          ...current,
          awaitingFullRead: false,
          afterFullRead: true,
          bodyText: body || current.bodyText || "",
        });
        return {
          state: nextState,
          speech: formatFullEmailSpeech(
            current.selectedEmail,
            body || current.bodyText || "",
          ),
          action: "none",
        };
      }

      if (isAshleyDeclinePhrase(transcript)) {
        const nextState = createGmailConversationState({
          ...current,
          awaitingFullRead: false,
          afterFullRead: false,
        });
        return {
          state: nextState,
          speech: EMAIL_WHAT_NEXT_AFTER,
          action: "none",
        };
      }
    }

    if (selection?.kind === "memory") {
      if (/\bread it again\b/.test(normalized) || current.afterFullRead) {
        return {
          state: createGmailConversationState({
            ...current,
            afterFullRead: true,
            awaitingFullRead: false,
          }),
          speech: formatFullEmailSpeech(
            current.selectedEmail,
            current.bodyText || extras.bodyText || "",
          ),
          action: current.bodyText || extras.bodyText ? "none" : "fetch_detail",
          messageId: current.selectedMessageId,
          pendingFullRead: true,
        };
      }
      return {
        state: createGmailConversationState({
          ...current,
          awaitingFullRead: true,
        }),
        speech: buildRichSummarySpeech(
          current.selectedEmail,
          current.bodyText || extras.bodyText,
        ),
        action: "none",
      };
    }

    // Allow selecting a different email while in single mode (same page).
    if (selection?.kind === "ordinal" || selection?.kind === "sender") {
      return applySelection(current, selection);
    }

    return {
      state: current,
      speech: formatEmailBrowseReprompt(),
      action: "none",
    };
  }

  // --- Review all mode ---
  if (current.browsingMode === "review_all") {
    const selection = resolveEmailSelection(transcript, current.messages);
    if (selection?.kind === "ordinal" || selection?.kind === "sender" || selection?.kind === "memory") {
      if (selection.kind === "memory" && current.selectedEmail) {
        return {
          state: selectEmailInState(current, current.selectedEmail, current.selectedOrdinal || 1),
          speech: buildRichSummarySpeech(current.selectedEmail, null),
          action: current.selectedEmail?.id ? "fetch_detail" : "none",
          messageId: current.selectedEmail?.id || null,
        };
      }
      if (selection.kind !== "memory") {
        return applySelection(current, selection);
      }
    }

    const choice = resolveEmailBrowseChoice(transcript, {
      allowSummarize: !current.afterSummary,
      allowNext: false,
      allowReviewAll: false,
    });
    if (choice === "done") {
      return { state: null, speech: "", action: "close" };
    }
    if (choice === "open") {
      return { state: null, speech: "", action: "open_inbox" };
    }
    if (choice === "summarize" && !current.afterSummary) {
      const highlight = getEmailPage(current.messages, 0, EMAIL_PAGE_SIZE);
      const detail = formatEmailDetailSummary(highlight);
      const nextState = createGmailConversationState({
        ...current,
        browsingMode: "review_all",
        afterSummary: true,
      });
      return {
        state: nextState,
        speech: `${detail} ${formatEmailBrowseOffer({ afterAction: true })}`,
        action: "none",
      };
    }
    return {
      state: current,
      speech: formatEmailBrowseReprompt(),
      action: "none",
    };
  }

  // --- Browse mode: prefer single-email selection before batch choices ---
  const page = getActiveBrowsePage(current);
  const selection = resolveEmailSelection(transcript, page);
  if (selection?.kind === "ordinal" || selection?.kind === "sender") {
    return applySelection(current, selection);
  }
  if (selection?.kind === "memory" && current.selectedEmail) {
    const nextState = selectEmailInState(
      current,
      current.selectedEmail,
      current.selectedOrdinal || 1,
    );
    return {
      state: nextState,
      speech: buildRichSummarySpeech(current.selectedEmail, current.bodyText),
      action: current.selectedEmail?.id ? "fetch_detail" : "none",
      messageId: current.selectedEmail?.id || null,
    };
  }

  const hasNext = hasEmailNextPage(
    current.messages,
    current.offset,
    current.pageSize || EMAIL_PAGE_SIZE,
  );
  const choice = resolveEmailBrowseChoice(transcript, {
    allowSummarize: !current.afterSummary,
    allowNext: hasNext,
    allowReviewAll: true,
  });

  if (choice === "done") {
    return { state: null, speech: "", action: "close" };
  }
  if (choice === "open") {
    return { state: null, speech: "", action: "open_inbox" };
  }
  if (choice === "review_all") {
    const nextState = createGmailConversationState({
      messages: current.messages,
      browsingMode: "review_all",
      afterSummary: false,
    });
    return {
      state: nextState,
      speech: formatAllUnreadSenders(current.messages),
      action: "none",
    };
  }
  if (choice === "summarize" && !current.afterSummary) {
    const detail = formatEmailDetailSummary(page);
    const nextState = createGmailConversationState({
      ...current,
      afterSummary: true,
      browsingMode: "browse",
    });
    return {
      state: nextState,
      speech: `${detail} ${formatEmailBrowseOffer({ afterAction: true })}`,
      action: "none",
    };
  }
  if (choice === "next" && hasNext) {
    const nextOffset = current.offset + (current.pageSize || EMAIL_PAGE_SIZE);
    const nextState = createGmailConversationState({
      ...current,
      offset: nextOffset,
      afterSummary: false,
      browsingMode: "browse",
      selectedEmail: null,
      selectedMessageId: null,
      selectedOrdinal: null,
    });
    return {
      state: nextState,
      speech: browseSpeech(nextState),
      action: "none",
    };
  }

  return {
    state: current,
    speech: formatEmailBrowseReprompt(),
    action: "none",
  };
}

function applySelection(state, selection) {
  if (selection.kind === "ordinal") {
    const nextState = selectEmailInState(state, selection.message, selection.ordinal);
    return {
      state: nextState,
      speech: buildRichSummarySpeech(selection.message, null),
      action: selection.message?.id ? "fetch_detail" : "none",
      messageId: selection.message?.id || null,
    };
  }

  if (selection.kind === "sender") {
    const matches = selection.matches || [];
    if (matches.length === 0) {
      return {
        state: state,
        speech: formatEmailNotFoundOnPage(),
        action: "none",
      };
    }
    if (matches.length > 1) {
      const display =
        matches[0]?.fromName || matches[0]?.from || selection.senderQuery || "that sender";
      const nextState = setAmbiguousSenderState(state, {
        senderKey: display,
        matches,
      });
      return {
        state: nextState,
        speech: formatAmbiguousSenderClarification(display, matches.length),
        action: "none",
      };
    }
    const message = matches[0];
    const page = getActiveBrowsePage(state);
    const ordinal = page.findIndex((m) => m === message || m?.id === message?.id) + 1;
    const nextState = selectEmailInState(state, message, ordinal > 0 ? ordinal : 1);
    return {
      state: nextState,
      speech: buildRichSummarySpeech(message, null),
      action: message?.id ? "fetch_detail" : "none",
      messageId: message?.id || null,
    };
  }

  return {
    state,
    speech: formatEmailBrowseReprompt(),
    action: "none",
  };
}

/**
 * Apply fetched body into state and produce rich summary or full read.
 * @param {import("../../connectedServices/email/gmailConversationState").GmailConversationState} state
 * @param {string} bodyText
 * @param {{ speakFull?: boolean }} [options]
 */
export function applyFetchedEmailBody(state, bodyText, options = {}) {
  const nextState = createGmailConversationState({
    ...state,
    bodyText: bodyText || "",
    awaitingFullRead: !options.speakFull,
    afterFullRead: Boolean(options.speakFull),
  });
  if (options.speakFull) {
    return {
      state: nextState,
      speech: formatFullEmailSpeech(state.selectedEmail, bodyText || ""),
      action: "none",
    };
  }
  return {
    state: nextState,
    speech: buildRichSummarySpeech(state.selectedEmail, bodyText),
    action: "none",
  };
}
